import React, { FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import styles from "../passwordReset.module.css";

const AUTH_API_URL =
  process.env.REACT_APP_AUTH_ENDPOINT || "http://localhost:8080";

type Result =
  | { status: "idle" }
  | { status: "error"; message: string }
  | { status: "success" };

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<Result>(
    token
      ? { status: "idle" }
      : {
          status: "error",
          message:
            "This page must be opened from the password reset link in your email.",
        }
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting || !token) return;
    if (password !== confirmation) {
      setResult({ status: "error", message: "Passwords do not match." });
      return;
    }

    setSubmitting(true);
    setResult({ status: "idle" });
    try {
      const response = await fetch(`${AUTH_API_URL}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setPassword("");
        setConfirmation("");
        setResult({ status: "success" });
      } else {
        setResult({
          status: "error",
          message:
            data?.detail ||
            "This password reset link is invalid, expired, or already used.",
        });
      }
    } catch {
      setResult({
        status: "error",
        message:
          "We couldn't reach the password reset service. Check your connection and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <main className={styles.card}>
        <Link to="/" className={styles.brand}>
          DriveNow
        </Link>
        <div className={styles.tagline}>Peer-to-peer car marketplace</div>

        {result.status === "success" ? (
          <>
            <h1 className={styles.title}>Password updated</h1>
            <p className={styles.success}>
              Your password was reset successfully. You can now sign in with
              your new password.
            </p>
            <Link to="/login" className={styles.primaryButton}>
              Sign in
            </Link>
          </>
        ) : (
          <>
            <h1 className={styles.title}>Choose a new password</h1>
            <p className={styles.body}>
              Use at least 8 characters. This link can only be used once.
            </p>
            {result.status === "error" && (
              <p role="alert" className={styles.error}>
                {result.message}
              </p>
            )}
            {token ? (
              <form onSubmit={handleSubmit}>
                <div className={styles.field}>
                  <label htmlFor="new-password">New password</label>
                  <input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    maxLength={128}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="confirm-password">Confirm new password</label>
                  <input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    maxLength={128}
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                    required
                    disabled={submitting}
                  />
                </div>
                <button
                  type="submit"
                  className={styles.primaryButton}
                  disabled={submitting}
                >
                  {submitting ? "Updating…" : "Reset password"}
                </button>
              </form>
            ) : null}
            <Link to="/forgot-password" className={styles.secondaryButton}>
              Request a new link
            </Link>
          </>
        )}
      </main>
    </div>
  );
};

export default ResetPasswordPage;
